/**
 * H3 hex density overlay layer.
 *
 * Renders a GeoJSON fill layer of hexagonal cells colored by vehicle
 * density. Positioned below all vehicle layers as a heatmap underlay.
 * Click a hex cell to inspect its details (desktop only).
 */

import { memo, useMemo, useState, useCallback, useEffect } from 'react';
import { Source, Layer, Popup, useMap } from 'react-map-gl/maplibre';
import type { FillLayerSpecification, LineLayerSpecification, MapLayerMouseEvent } from 'maplibre-gl';

import { useSettingsStore } from '../../stores/settingsStore';
import { useHexDensity } from '../../hooks/useHexDensity';
import { useIsMobile } from '../../hooks/useIsMobile';

const HEX_SOURCE_ID = 'hex-density-source';
const HEX_FILL_LAYER_ID = 'hex-density-fill';
const HEX_LINE_LAYER_ID = 'hex-density-line';

interface HexPopupData {
  longitude: number;
  latitude: number;
  cellId: string;
  count: number;
  journeyIds: string[];
}

export const HexDensityLayer = memo(function HexDensityLayer() {
  const enabled = useSettingsStore((state) => state.hexDensityEnabled);
  const geojson = useHexDensity(enabled);
  const isMobile = useIsMobile();
  const { current: map } = useMap();
  const [popup, setPopup] = useState<HexPopupData | null>(null);

  // Fill: step color by count (green -> yellow -> orange -> red)
  const fillStyle: FillLayerSpecification = useMemo(
    () => ({
      id: HEX_FILL_LAYER_ID,
      type: 'fill',
      source: HEX_SOURCE_ID,
      paint: {
        'fill-color': [
          'step',
          ['get', 'count'],
          '#22c55e',  // 0+: green
          3, '#eab308',  // 3+: yellow
          8, '#f97316',  // 8+: orange
          15, '#ef4444',  // 15+: red
        ],
        'fill-opacity': 0.25,
      },
    }),
    []
  );

  // Thin hex border for visual clarity
  const lineStyle: LineLayerSpecification = useMemo(
    () => ({
      id: HEX_LINE_LAYER_ID,
      type: 'line',
      source: HEX_SOURCE_ID,
      paint: {
        'line-color': '#6b7280',
        'line-width': 0.5,
        'line-opacity': 0.3,
      },
    }),
    []
  );

  // Vehicle layer IDs that take priority over hex click
  const VEHICLE_LAYERS = ['vehicles-icons', 'vehicles-background', 'vehicles-labels'];

  // Click handler for hex cells (desktop only, yields to vehicle markers)
  const handleHexClick = useCallback(
    (e: MapLayerMouseEvent) => {
      if (!map) return;

      // If a vehicle marker is under the cursor, don't intercept
      const vehicleFeatures = map.queryRenderedFeatures(e.point, { layers: VEHICLE_LAYERS });
      if (vehicleFeatures.length > 0) return;

      const feature = e.features?.[0];
      if (!feature?.properties) return;

      const props = feature.properties;
      const journeyIdsStr = props.journeyIds as string | undefined;

      setPopup({
        longitude: e.lngLat.lng,
        latitude: e.lngLat.lat,
        cellId: props.cellId ?? '',
        count: props.count ?? 0,
        journeyIds: journeyIdsStr ? journeyIdsStr.split(',').filter(Boolean) : [],
      });
    },
    [map]
  );

  // Dismiss popup on map click outside hex
  const handleMapClick = useCallback(
    (e: MapLayerMouseEvent) => {
      if (!map) return;
      const features = map.queryRenderedFeatures(e.point, {
        layers: [HEX_FILL_LAYER_ID],
      });
      if (features.length === 0) {
        setPopup(null);
      }
    },
    [map]
  );

  // Pointer cursor on hex hover
  const handleMouseEnter = useCallback(() => {
    if (map) map.getCanvas().style.cursor = 'pointer';
  }, [map]);

  const handleMouseLeave = useCallback(() => {
    if (map) map.getCanvas().style.cursor = '';
  }, [map]);

  // Register map event listeners (desktop only)
  useEffect(() => {
    if (!map || !enabled || isMobile) return;

    map.on('click', HEX_FILL_LAYER_ID, handleHexClick);
    map.on('click', handleMapClick);
    map.on('mouseenter', HEX_FILL_LAYER_ID, handleMouseEnter);
    map.on('mouseleave', HEX_FILL_LAYER_ID, handleMouseLeave);

    return () => {
      map.off('click', HEX_FILL_LAYER_ID, handleHexClick);
      map.off('click', handleMapClick);
      map.off('mouseenter', HEX_FILL_LAYER_ID, handleMouseEnter);
      map.off('mouseleave', HEX_FILL_LAYER_ID, handleMouseLeave);
      map.getCanvas().style.cursor = '';
      setPopup(null);
    };
  }, [map, enabled, isMobile, handleHexClick, handleMapClick, handleMouseEnter, handleMouseLeave]);

  if (!enabled) return null;

  return (
    <>
      <Source id={HEX_SOURCE_ID} type="geojson" data={geojson}>
        <Layer {...fillStyle} />
        <Layer {...lineStyle} />
      </Source>

      {popup && (
        <Popup
          longitude={popup.longitude}
          latitude={popup.latitude}
          anchor="bottom"
          closeOnClick={false}
          onClose={() => setPopup(null)}
          maxWidth="260px"
        >
          <div className="p-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">
                {popup.count} vehicle{popup.count !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-1 font-mono">
              {popup.cellId.slice(0, 12)}{popup.cellId.length > 12 ? '\u2026' : ''}
            </div>
            {popup.journeyIds.length > 0 && (
              <div className="text-[10px] text-gray-600 dark:text-gray-300 max-h-24 overflow-y-auto">
                {popup.journeyIds.slice(0, 10).map((id) => (
                  <div key={id} className="truncate font-mono">{id}</div>
                ))}
                {popup.journeyIds.length > 10 && (
                  <div className="text-gray-400 italic">
                    +{popup.journeyIds.length - 10} more
                  </div>
                )}
              </div>
            )}
          </div>
        </Popup>
      )}
    </>
  );
});
